/**
 * Per-user flags stored in settingsJson (managed via admin user management).
 */

import { isProvider, resolveProviderLevel, type RoleProfile } from '../rbac/member-roles'

export type UserSettingsFlags = {
  /** Team leader may book on-call periods when true. */
  canBeOnCall?: boolean
  /** Receives urgent help / management on-call rings when true. */
  urgentHelpReceiver?: boolean
  /** May book daytime HQ call-receiver windows when true. */
  daytimeCallReceiver?: boolean
  /** May record whole in-person sessions for an AI progress-note draft. */
  premiumSessionRecording?: boolean
}

/**
 * Admin-only switch for Premium Session Recording (whole-session recording →
 * AI progress-note draft). Set per user in User management; every
 * session-recording API route re-checks it server-side.
 */
export const PREMIUM_SESSION_RECORDING_KEY = 'premiumSessionRecording' as const

export const CONTACT_FLAG_KEYS = [
  'canBeOnCall',
  'urgentHelpReceiver',
  'daytimeCallReceiver',
] as const

export type ContactFlagKey = (typeof CONTACT_FLAG_KEYS)[number]

/**
 * Minimum provider level at which each contact flag is FUNCTIONAL, derived
 * from how the rest of the system consumes them (do not tighten/loosen
 * without checking those call sites):
 *
 * - `canBeOnCall` / `daytimeCallReceiver`: booking requires the org's
 *   bookable-window config + peer bookings, which the directory only shares
 *   with L3+ providers (`canViewDirectorySchedulingSettings` /
 *   `canViewDirectorySettingsJson` in list-users-auth). An L2 Provider Basic
 *   with the flag can OPEN the on-call page but can never book — the exact
 *   inconsistent state this map exists to prevent.
 * - `urgentHelpReceiver`: receivers are rung, not bookers — any provider
 *   works (`getUrgentHelpReceivers` filters `isProvider` + flag, with an L4
 *   fallback), so the floor is the provider baseline (level 2).
 *
 * Non-provider roles (members / independents) support no contact flags —
 * the flags editor already renders only for providers.
 */
export const CONTACT_FLAG_MIN_PROVIDER_LEVEL: Record<ContactFlagKey, number> = {
  canBeOnCall: 3,
  urgentHelpReceiver: 2,
  daytimeCallReceiver: 3,
}

/** Whether this role can meaningfully hold the given contact flag. */
export function roleSupportsContactFlag(profile: RoleProfile, flag: ContactFlagKey): boolean {
  if (!isProvider(profile)) return false
  return resolveProviderLevel(profile) >= CONTACT_FLAG_MIN_PROVIDER_LEVEL[flag]
}

/**
 * Contact flags a settings patch tries to ENABLE that the role does not
 * support. Disabling a flag is always allowed (that is how an inconsistent
 * legacy state gets cleaned up).
 */
export function unsupportedContactFlagEnables(
  profile: RoleProfile,
  patch: Record<string, unknown>,
): ContactFlagKey[] {
  return CONTACT_FLAG_KEYS.filter(
    (flag) => patch[flag] === true && !roleSupportsContactFlag(profile, flag),
  )
}

/**
 * Contact flags currently set on `settingsJson` that the given role does not
 * support — used to strip stale flags when an admin downgrades a role.
 */
export function contactFlagsToStripForRole(
  profile: RoleProfile,
  settingsJson: unknown,
): ContactFlagKey[] {
  const root = parseSettingsRoot(settingsJson)
  return CONTACT_FLAG_KEYS.filter(
    (flag) => root[flag] === true && !roleSupportsContactFlag(profile, flag),
  )
}

function parseSettingsRoot(raw: unknown): Record<string, unknown> {
  if (!raw) return {}
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw) as Record<string, unknown>
    } catch {
      return {}
    }
  }
  return raw as Record<string, unknown>
}

/**
 * Premium Session Recording only makes sense for someone who can be the
 * assigned provider on a session, so the switch is limited to provider roles.
 */
export function roleSupportsPremiumSessionRecording(profile: RoleProfile): boolean {
  return isProvider(profile)
}

/**
 * Every admin-only flag currently set on `settingsJson` that the given role
 * can no longer hold — used to strip stale flags when an admin changes a role.
 */
export function settingsFlagsToStripForRole(
  profile: RoleProfile,
  settingsJson: unknown,
): string[] {
  const stale: string[] = contactFlagsToStripForRole(profile, settingsJson)
  const root = parseSettingsRoot(settingsJson)
  if (root[PREMIUM_SESSION_RECORDING_KEY] === true && !roleSupportsPremiumSessionRecording(profile)) {
    stale.push(PREMIUM_SESSION_RECORDING_KEY)
  }
  return stale
}

export function getUserSettingsFlags(
  user: { settingsJson?: unknown } | null | undefined,
): UserSettingsFlags {
  const root = parseSettingsRoot(user?.settingsJson)
  return {
    canBeOnCall: root.canBeOnCall === true,
    urgentHelpReceiver: root.urgentHelpReceiver === true,
    daytimeCallReceiver: root.daytimeCallReceiver === true,
    premiumSessionRecording: root[PREMIUM_SESSION_RECORDING_KEY] === true,
  }
}

/**
 * True when the user holds Premium Session Recording. The flag only counts on
 * a provider role, so a stale flag left on a downgraded account grants nothing.
 */
export function userHasPremiumSessionRecording(
  user: (RoleProfile & { settingsJson?: unknown }) | null | undefined,
): boolean {
  if (!user || !roleSupportsPremiumSessionRecording(user)) return false
  return getUserSettingsFlags(user).premiumSessionRecording === true
}

export function userCanBeOnCall(user: { settingsJson?: unknown } | null | undefined): boolean {
  return getUserSettingsFlags(user).canBeOnCall === true
}

export function userIsUrgentHelpReceiver(
  user: { settingsJson?: unknown } | null | undefined,
): boolean {
  return getUserSettingsFlags(user).urgentHelpReceiver === true
}

export function userIsDaytimeCallReceiver(
  user: { settingsJson?: unknown } | null | undefined,
): boolean {
  return getUserSettingsFlags(user).daytimeCallReceiver === true
}
